import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { VERTICALS } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/businesses", label: "Businesses" },
  { to: "/leadership", label: "Leadership" },
  { to: "/projects", label: "Upcoming Projects" },
  { to: "/gallery", label: "Gallery" },
  { to: "/careers", label: "Careers" },
  { to: "/news", label: "News" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-2xl bg-white/85 border-b border-slate-200/60 shadow-[0_2px_20px_rgba(0,0,0,0.04)]" : "bg-transparent"
      }`}
    >
      <div className="container-page flex items-center justify-between h-[72px] md:h-[84px]">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-3 group">
          <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-full bg-white ring-1 ring-slate-200 shadow-sm flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
            <img src="/logo.png" alt="Shramik Group" className="w-[92%] h-[92%] object-contain" />
          </div>
          <div className="leading-tight">
            <div className={`font-display font-bold text-lg tracking-tight transition-colors ${scrolled ? "text-shramik-ink" : "text-white drop-shadow-sm"}`}>SHRAMIK</div>
            <div className={`text-[10px] tracking-[0.28em] font-semibold uppercase transition-colors ${scrolled ? "text-shramik-green" : "text-shramik-gold"}`}>Group</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium link-underline transition-colors ${
                  isActive
                    ? (scrolled ? "text-shramik-green" : "text-shramik-gold")
                    : (scrolled ? "text-slate-700 hover:text-shramik-green" : "text-white/90 hover:text-white")
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="header-cta" className="btn-primary">
            Contact Us <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${scrolled || open ? "bg-white border-slate-200 text-shramik-ink" : "bg-white/15 border-white/25 text-white backdrop-blur-md"}`}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="container-page py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({isActive}) => `py-3 border-b border-slate-100 text-base font-medium ${isActive ? "text-shramik-green" : "text-slate-800"}`}>{n.label}</NavLink>
              ))}
              <div className="pt-2 pb-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Business Verticals</div>
                <div className="grid grid-cols-1 gap-1">
                  {VERTICALS.map((v) => (
                    <Link key={v.slug} to={`/businesses/${v.slug}`} className="text-sm text-slate-700 hover:text-shramik-green py-1.5 flex items-center justify-between">
                      {v.name} <ChevronRight className="w-4 h-4 text-slate-400" />
                    </Link>
                  ))}
                </div>
              </div>
              <Link to="/contact" className="btn-primary justify-center">Contact Us</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
